import { useStore, useStoreMap } from 'effector-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { driverModel } from '~/entities/common/Driver'
import { aggregatorDriverModel } from '~/entities/common/Driver/Aggregator'
import {
  EditYandexAggregator,
  editYandexAggregatorModel,
} from '~/features/common/Drivers/Aggregators/EditYandexAggregator'
import { Breadcrumbs, BreadcrumbsList } from '~/shared/ui/Breadcrumbs'
import { Container, PageContainer, PageTitle } from '~/shared/ui/Layout'
import { NotFound } from '~/shared/ui/NotFound'

export const EditYandexAggregatorPage = () => {
  const { t } = useTranslation()

  const params = useParams<{ aggregatorId: string; driverId: string }>()
  const aggregatorId = params.aggregatorId as string
  const driverId = params.driverId as string

  const driverFullName = useStoreMap(driverModel.$driver, (driver) =>
    driver?.getFullName(),
  )
  const aggregatorDriver = useStore(editYandexAggregatorModel.$aggregatorDriver)

  const aggregatorDriverId = aggregatorDriver?.getDriverId()
  const title = aggregatorDriver?.getAggregatorAccount().getTitle() || '...'

  const isLoading = useStore(aggregatorDriverModel.requestFx.pending)

  const breadcrumbs: BreadcrumbsList = useMemo(
    () => [
      {
        titleI18n: 'drivers',
        link: '/drivers',
      },
      {
        title: driverFullName || '...',
        link: `/drivers/${driverId}/details`,
      },
      {
        titleI18n: 'aggregators',
        link: `/drivers/${driverId}/aggregators`,
      },
      { title },
    ],
    [driverFullName, driverId, title],
  )

  const yandexAggregatorError =
    aggregatorDriverModel.useAggregatorDriverError(aggregatorId)

  if (yandexAggregatorError) {
    return (
      <NotFound message={t('alert.failed to load data of yandex aggregator')} />
    )
  }

  if (
    !isLoading &&
    aggregatorDriver &&
    aggregatorDriver.getApiId() === aggregatorId &&
    driverId !== aggregatorDriverId
  ) {
    return (
      <NotFound
        message={t(
          'alert.loaded yandex aggregator with aggregatorId does not correspond to the current driver with driverId',
          { aggregatorId, driverId },
        )}
      />
    )
  }

  return (
    <PageContainer title={t('card of yandex aggregator')}>
      <Container isHeader>
        <Breadcrumbs list={breadcrumbs} lastLoading={isLoading} />
        <PageTitle loading={isLoading}>{title}</PageTitle>
      </Container>
      <Container>
        <EditYandexAggregator aggregatorId={aggregatorId} />
      </Container>
    </PageContainer>
  )
}
